"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { useTaskContext } from "@/contexts/TaskContext";
// ConnectionDot hidden in demo mode — it shows a gray dot that reads
// as a bug. Will return when live mode is the default.
// import ConnectionDot from "./ConnectionDot";
import { useBotworkContext } from "@/contexts/BotworkContext";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import DesktopSidebar from "./DesktopSidebar";
import type { ConversationSummary } from "@/hooks/useChatHistory";

// ── Props (all optional — reads from context) ────────────────────

interface SidebarProps {
  conversations?: ConversationSummary[];
  activeId?: string | null;
  onNewTask?: () => void;
  onSelect?: (id: string) => void;
  onSignIn?: () => void;
  signedIn?: boolean;
}

const F = "'Plus Jakarta Sans', sans-serif";
const COLLAPSE_KEY = "botwork.sidebarCollapsed";

// ── Main Component ───────────────────────────────────────────────

export default function Sidebar(props: SidebarProps = {}) {
  const isDesktop = useIsDesktop();
  if (isDesktop) return <DesktopSidebar />;
  return <WebSidebar {...props} />;
}

function WebSidebar(_props: SidebarProps = {}) {
  const ctx = useTaskContext();
  const botwork = useBotworkContext();
  const router = useRouter();
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const v = window.localStorage.getItem(COLLAPSE_KEY);
    if (v === "1") setCollapsed(true);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    if (typeof window === "undefined") return;
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed, hydrated]);

  const onNewTask = () => {
    // Reset the task machine and make sure we're on /app. If we're already
    // there and idle, the RESET is a no-op (reducer returns initialState).
    ctx.dispatch({ type: "RESET" });
    if (pathname !== "/app") router.push("/app");
  };
  const onSignIn = () => ctx.openLogin();
  const onSelect = (_id: string) => {
    ctx.dispatch({ type: "RESET" });
    if (pathname !== "/app") router.push("/app");
  };
  const signedIn = ctx.user.isSignedIn;
  // Dedupe recents by (normalised) title — keep the most-recent entry.
  const seenTitles = new Set<string>();
  const conversations = ctx.user.taskHistory
    .filter((h) => {
      const key = h.title.trim().toLowerCase();
      if (seenTitles.has(key)) return false;
      seenTitles.add(key);
      return true;
    })
    .map((h) => ({
      id: h.id,
      title: h.title,
      agentName: h.agentName,
    }));

  return (
    <aside
      style={{
        width: collapsed ? 91 : 260,
        background: "#171717",
        paddingTop: 32,
        paddingBottom: 24,
        paddingLeft: 16,
        paddingRight: 16,
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        flexShrink: 0,
        overflow: "hidden",
        transition: "width 200ms ease-out",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          paddingBottom: 32,
          width: "100%",
          flexShrink: 0,
          gap: 8,
        }}
      >
        {!collapsed && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img
              src="/logo-botwork.svg"
              alt="BotWork"
              style={{ height: 24, width: "auto", filter: "brightness(0) invert(1)" }}
            />
            {/* ConnectionDot hidden in demo mode. */}
          </div>
        )}
        {collapsed ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <button
              onClick={() => setCollapsed((c) => !c)}
              aria-label="Expand sidebar"
              className="app-close-btn"
              style={{
                width: 24,
                height: 24,
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              <img src="/app-icons/web-section-alt.svg" alt="" style={{ width: 24, height: 24 }} />
            </button>
            {/* ConnectionDot hidden in demo mode. */}
          </div>
        ) : (
          <button
            onClick={() => setCollapsed((c) => !c)}
            aria-label="Collapse sidebar"
            className="app-close-btn"
            style={{
              width: 24,
              height: 24,
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
          >
            <img src="/app-icons/web-section-alt.svg" alt="" style={{ width: 24, height: 24 }} />
          </button>
        )}
      </div>

      {/* Scrollable middle */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 40,
          flex: "1 0 0",
          minHeight: 0,
          overflow: "clip",
          width: "100%",
        }}
      >
        <nav
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            alignItems: collapsed ? "center" : "flex-start",
            // Shift nav buttons 6 px left of the aside's px-16 gutter
            // when expanded. Collapsed state keeps centre alignment.
            // NavButton inner wraps in transform:scale(0.95), so the
            // visible button is 5 % narrower than the nav box. To hit
            // pl-10/pr-16 visually we need nav width `calc(100%+12px)`
            // (not +6): the extra 6 px on each side compensates the
            // 3 % shrink per side, leaving 16 px of true right gap.
            marginLeft: collapsed ? 0 : -6,
            width: collapsed ? "100%" : "calc(100% + 12px)",
            flexShrink: 0,
          }}
        >
          <NavButton icon="plus-circle" label="New task" filled onClick={onNewTask} collapsed={collapsed} />

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              alignItems: collapsed ? "center" : "flex-start",
              width: "100%",
            }}
          >
            <NavButton icon="search" label="Search" href="/app/search" collapsed={collapsed} />
            <NavButton icon="shop" label="Marketplace" href="/app/marketplace" collapsed={collapsed} />
            <NavButton icon="bookmark" label="Bookmarked" href="/app/bookmarks" collapsed={collapsed} />
            <NavButton icon="clock" label="Recents" href="/app/history" collapsed={collapsed} />
            <NavButton icon="folder-open" label="Assets" href="/app/assets" collapsed={collapsed} />
            <NavButton icon="location-arrow" label="Network" href="/app/network" collapsed={collapsed} />
            <NavButton icon="star-large-outline" label="Training" href="/app/training" collapsed={collapsed} />
          </div>
        </nav>

        {!collapsed && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start", width: "100%", flexShrink: 0 }}>
            <div style={{ opacity: 0.6, width: "100%" }}>
              <p
                style={{
                  fontFamily: F,
                  fontWeight: 700,
                  fontSize: 10,
                  letterSpacing: "1px",
                  lineHeight: "15px",
                  color: "#CDC2D8",
                  margin: 0,
                }}
              >
                RECENTS
              </p>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                alignItems: "flex-start",
                paddingTop: 12,
                paddingBottom: 12,
                width: "100%",
              }}
            >
              {conversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => onSelect(conv.id)}
                  className="app-recent-item"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%",
                    background: "none",
                    border: "none",
                    padding: "6px 8px",
                    borderRadius: 8,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <p
                    style={{
                      fontFamily: F,
                      fontWeight: 500,
                      fontSize: 14,
                      lineHeight: "20px",
                      color: "#E5E2E1",
                      margin: 0,
                      marginBottom: -1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%",
                    }}
                  >
                    {conv.title}
                  </p>
                  <p
                    style={{
                      fontFamily: F,
                      fontWeight: 400,
                      fontSize: 11,
                      lineHeight: "16.5px",
                      color: "#CDC2D8",
                      margin: 0,
                      marginBottom: -1,
                    }}
                  >
                    {conv.agentName}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom — the UserCard owns its own border-t so the divider can span
          the full sidebar width (Figma 76:7449). The collapsed states render
          plain avatar / icon with no divider per Figma 61:4231. */}
      <div style={{ flexShrink: 0, width: "100%" }}>
        {signedIn ? (
          <UserCard
            name={ctx.user.name}
            initials={ctx.user.initials}
            balance={botwork.mode === "live" ? botwork.balance : ctx.user.balance}
            collapsed={collapsed}
          />
        ) : (
          <SignInButton onClick={onSignIn} collapsed={collapsed} />
        )}
      </div>
    </aside>
  );
}

// ── NavButton ────────────────────────────────────────────────────

function NavButton({
  icon,
  label,
  filled,
  onClick,
  href,
  collapsed,
}: {
  icon: string;
  label: string;
  filled?: boolean;
  onClick?: () => void;
  href?: string;
  collapsed?: boolean;
}) {
  // Collapsed + filled = circular icon button (New task CTA).
  // Matches Figma 61:4245 — nested wrapper so the hit target + bounding box
  // align with the other collapsed nav buttons (42.8 grid).
  if (collapsed && filled) {
    return (
      <button
        onClick={onClick}
        aria-label={label}
        title={label}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 42.8,
          height: 42.8,
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
      >
        <div style={{ transform: "scale(0.95)" }}>
          <div
            className="app-nav-btn-new"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 12,
              borderRadius: 9999,
              background: "#2D2C2C",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 21.053, height: 21.053 }}>
              <span style={{ display: "flex", transform: "scale(1.05)" }}>
                <img src={`/app-icons/${icon}.svg`} alt="" style={{ width: 20, height: 20 }} />
              </span>
            </span>
          </div>
        </div>
      </button>
    );
  }

  const buttonInner = (
    <div
      className={filled ? "app-nav-btn-new" : "app-nav-btn"}
      style={{
        display: "flex",
        alignItems: "center",
        ...(collapsed
          ? { justifyContent: "center" }
          : filled
          ? { justifyContent: "space-between" }
          : { gap: 8 }),
        padding: 12,
        borderRadius: 9999,
        background: filled ? "#2D2C2C" : "transparent",
        width: "100%",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 21.053, height: 21.053, flexShrink: 0 }}>
        <span style={{ display: "flex", transform: "scale(1.05)" }}>
          <img src={`/app-icons/${icon}.svg`} alt="" style={{ width: 20, height: 20 }} />
        </span>
      </span>

      {!collapsed && (
        <>
          <span
            style={{
              fontFamily: F,
              fontWeight: 600,
              fontSize: 14,
              lineHeight: "21px",
              color: "#F6F5F5",
              whiteSpace: "nowrap",
              textAlign: "center",
              flexShrink: 0,
            }}
          >
            {label}
          </span>
          {filled && <span style={{ width: 21.053, height: 21.053, flexShrink: 0 }} />}
        </>
      )}
    </div>
  );

  // In collapsed mode the nav buttons don't need the 0.95 scale wrapper —
  // keep their bounding box aligned to the centred 42x42 grid.
  const wrapper = collapsed ? (
    <div style={{ display: "flex", height: 42.8, alignItems: "center", justifyContent: "center", width: 42.8 }}>
      {buttonInner}
    </div>
  ) : (
    <div style={{ display: "flex", height: 42.8, alignItems: "center", justifyContent: "center", width: "100%" }}>
      <div style={{ transform: "scale(0.95)", width: "100%" }}>{buttonInner}</div>
    </div>
  );

  if (href) {
    return (
      <Link
        href={href}
        title={collapsed ? label : undefined}
        aria-label={collapsed ? label : undefined}
        style={{ textDecoration: "none", display: "block", width: collapsed ? "auto" : "100%" }}
      >
        {wrapper}
      </Link>
    );
  }

  return (
    <div
      onClick={onClick}
      title={collapsed ? label : undefined}
      style={{
        cursor: onClick ? "pointer" : "default",
        width: collapsed ? "auto" : "100%",
      }}
    >
      {wrapper}
    </div>
  );
}

// ── Sign In Button ───────────────────────────────────────────────

function SignInButton({ onClick, collapsed }: { onClick: () => void; collapsed?: boolean }) {
  if (collapsed) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px 0",
          width: "100%",
        }}
      >
        <button
          onClick={onClick}
          aria-label="Sign in"
          title="Sign in"
          className="app-signin-btn"
          style={{
            width: 42,
            height: 42,
            borderRadius: 9999,
            border: "2px solid #2E2A33",
            background: "transparent",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          }}
        >
          <img src="/app-icons/signin.svg" alt="" style={{ width: 20, height: 20 }} />
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", height: 42.8, alignItems: "center", justifyContent: "center", width: "100%", paddingLeft: 16, paddingRight: 16 }}>
      <div style={{ transform: "scale(0.95)", width: "100%" }}>
        <button
          onClick={onClick}
          className="app-signin-btn"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            border: "2px solid #2E2A33",
            borderRadius: 9999,
            paddingLeft: 18,
            paddingRight: 18,
            paddingTop: 14,
            paddingBottom: 14,
            width: "100%",
            background: "transparent",
            cursor: "pointer",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 21.053, height: 21.053 }}>
            <span style={{ display: "flex", transform: "scale(1.05)" }}>
              <img src="/app-icons/signin.svg" alt="" style={{ width: 20, height: 20 }} />
            </span>
          </span>
          <span
            style={{
              fontFamily: F,
              fontWeight: 600,
              fontSize: 14,
              lineHeight: "21px",
              color: "#F6F5F5",
              whiteSpace: "nowrap",
              textAlign: "center",
            }}
          >
            Sign in
          </span>
          <span style={{ width: 21.053, height: 21.053 }} />
        </button>
      </div>
    </div>
  );
}

// ── User Card — Figma 76:7449 ──────────────────────────────────
//   bg #171717, border-t #2C2B2B (full width), p-24, gap-12
//   avatar(40) + middle(name 14 + "Balance: $X" 11) + chevron(24)
//   all three columns wrapped with scale(1.05) per Figma

function UserCard({
  name,
  initials,
  balance,
  collapsed,
}: {
  name: string;
  initials: string;
  balance: number;
  collapsed?: boolean;
}) {
  if (collapsed) {
    return (
      <div
        className="app-user-card"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px 0",
          width: "100%",
        }}
      >
        <div
          title={`${name} · Balance: $${balance.toFixed(2)}`}
          style={{
            width: 40,
            height: 40,
            borderRadius: 9999,
            background: "#8635F6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ fontFamily: F, fontWeight: 600, fontSize: 14, color: "#F6F5F5" }}>{initials}</span>
        </div>
      </div>
    );
  }

  // Escape the aside's 16px horizontal padding so the border-t divider
  // spans the full 260px sidebar width.
  return (
    <div
      className="app-user-card"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: 24,
        width: "auto",
        marginLeft: -16,
        marginRight: -16,
        borderTop: "1px solid #2C2B2B",
        background: "#171717",
      }}
    >
      {/* Avatar — 40×40 inside 42.105 wrapper scaled 1.05 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 42.105,
          height: 42.105,
          flexShrink: 0,
        }}
      >
        <div style={{ transform: "scale(1.05)" }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 9999,
              background: "#8635F6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{ fontFamily: F, fontWeight: 600, fontSize: 14, lineHeight: "21px", color: "#F6F5F5" }}>
              {initials}
            </span>
          </div>
        </div>
      </div>

      {/* Middle — name + "Balance: $X" inside scale(1.05) wrapper */}
      <div style={{ flex: "1 0 0", minWidth: 0, display: "flex", alignItems: "center" }}>
        <div style={{ transform: "scale(1.05)", width: "100%", transformOrigin: "left center" }}>
          <p
            style={{
              fontFamily: F,
              fontWeight: 600,
              fontSize: 14,
              lineHeight: "21px",
              color: "#F6F5F5",
              margin: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </p>
          <p
            style={{
              fontFamily: F,
              fontWeight: 400,
              fontSize: 11,
              lineHeight: "16.5px",
              color: "#CDC2D8",
              margin: 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            Balance: ${balance.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Chevron — 24px vector inside 25.263 scale(1.05) wrapper.
          The Figma asset has a tight viewBox, so we render it small inside
          a 24-square centre. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 25.263,
          height: 25.263,
          flexShrink: 0,
        }}
      >
        <div style={{ transform: "scale(1.05)", display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, opacity: 0.7 }}>
          <img src="/app-icons/angle-down.svg" alt="" style={{ width: 11, height: 6.5 }} />
        </div>
      </div>
    </div>
  );
}
